// Library search: the top-bar box filters the contents panel as you type and, after a short pause,
// asks the server for full-text matches with a snippet. Enter opens the first result.

interface Hit {
  slug: string;
  url: string;
  title: string;
  section: string;
  snippet: string;
}

export function initSearch(): void {
  const box = document.querySelector<HTMLElement>('[data-learn-search]');
  if (!box) return;
  const input = box.querySelector<HTMLInputElement>('input');
  const results = box.querySelector<HTMLElement>('.lb-search-results');
  if (!input || !results) return;
  const rows = Array.from(document.querySelectorAll<HTMLElement>('[data-learn-nav-row]'));
  const sections = Array.from(document.querySelectorAll<HTMLDetailsElement>('[data-learn-nav-section]'));
  const empty = document.querySelector<HTMLElement>('[data-learn-nav-empty]');
  let timer = 0;
  let hits: Hit[] = [];

  const filterNav = (q: string) => {
    const needle = q.trim().toLowerCase();
    let shown = 0;
    rows.forEach((row) => {
      const match = !needle || (row.dataset.title || '').includes(needle);
      row.hidden = !match;
      if (match) shown++;
    });
    sections.forEach((sec) => {
      const any = Array.from(sec.querySelectorAll<HTMLElement>('[data-learn-nav-row]')).some((r) => !r.hidden);
      sec.hidden = !!needle && !any;
      if (needle && any) sec.open = true;
    });
    if (empty) empty.hidden = !needle || shown > 0;
  };

  // Results are built as DOM nodes with textContent, never as markup, so a snippet can only ever
  // be text.
  const render = () => {
    results.replaceChildren();
    if (hits.length === 0) {
      results.hidden = true;
      return;
    }
    hits.forEach((h, i) => {
      const a = document.createElement('a');
      a.className = 'lb-search-hit' + (i === 0 ? ' is-first' : '');
      a.href = h.url;
      a.setAttribute('role', 'option');
      const title = document.createElement('span');
      title.className = 'lb-search-hit-title';
      title.textContent = h.title;
      const section = document.createElement('span');
      section.className = 'lb-search-hit-section';
      section.textContent = h.section;
      const snippet = document.createElement('span');
      snippet.className = 'lb-search-hit-snippet';
      snippet.textContent = h.snippet;
      a.append(title, section, snippet);
      results.appendChild(a);
    });
    results.hidden = false;
  };

  const query = async (q: string) => {
    if (q.trim().length < 2) {
      hits = [];
      render();
      return;
    }
    try {
      const res = await fetch(`/api/learn/search?q=${encodeURIComponent(q.trim())}`, { credentials: 'same-origin' });
      if (!res.ok) return;
      const raw = (await res.json()) as Record<string, string>[];
      hits = raw.map((r) => ({
        slug: r.slug ?? r.Slug,
        url: r.url ?? r.Url,
        title: r.title ?? r.Title,
        section: r.section ?? r.Section,
        snippet: r.snippet ?? r.Snippet,
      }));
      render();
    } catch {
      /* offline: the nav filter still works */
    }
  };

  input.addEventListener('input', () => {
    filterNav(input.value);
    window.clearTimeout(timer);
    timer = window.setTimeout(() => query(input.value), 220);
  });
  input.addEventListener('keydown', (e) => {
    if (e.key === 'Enter' && hits.length > 0) {
      window.location.href = hits[0].url;
    } else if (e.key === 'Escape') {
      input.value = '';
      filterNav('');
      hits = [];
      render();
      input.blur();
    }
  });
  input.addEventListener('focus', () => {
    if (hits.length) results.hidden = false;
  });
  document.addEventListener('mousedown', (e) => {
    if (!box.contains(e.target as Node)) results.hidden = true;
  });
  // Cmd/Ctrl+K focuses the box, like every docs site.
  document.addEventListener('keydown', (e) => {
    if ((e.metaKey || e.ctrlKey) && e.key.toLowerCase() === 'k') {
      e.preventDefault();
      input.focus();
      input.select();
    }
  });
}
